function initHorizontalCarousel(root, config) {
  if (!root || root.dataset.bbsCarouselInit === 'true') return;

  const viewport = root.querySelector(config.viewportSelector);
  const track = root.querySelector(config.trackSelector);
  const prevBtn = config.prevSelector ? root.querySelector(config.prevSelector) : null;
  const nextBtn = config.nextSelector ? root.querySelector(config.nextSelector) : null;
  const dotsRoot = config.dotsSelector ? root.querySelector(config.dotsSelector) : null;
  const itemSelector = config.itemSelector;

  if (!viewport || !track) return;

  root.dataset.bbsCarouselInit = 'true';

  const autoplayEnabled = root.dataset.autoplay !== 'false' && config.autoplay !== false;
  const autoplayInterval = Number(root.dataset.autoplayInterval || config.autoplayInterval || 5000);

  const getItems = () => Array.from(track.querySelectorAll(itemSelector));

  const getVisibleCount = () => {
    const item = getItems()[0];
    if (!item) return 1;

    const styles = window.getComputedStyle(track);
    const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
    const itemWidth = item.getBoundingClientRect().width + gap;
    return Math.max(1, Math.floor(viewport.clientWidth / itemWidth));
  };

  const getPageWidth = () => {
    const item = getItems()[0];
    if (!item) return 0;

    const styles = window.getComputedStyle(track);
    const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
    const itemWidth = item.getBoundingClientRect().width + gap;
    return itemWidth * getVisibleCount();
  };

  const scrollByPage = (direction) => {
    const pageWidth = getPageWidth();
    if (!pageWidth) return;

    const maxScroll = track.scrollWidth - viewport.clientWidth;
    if (direction > 0 && viewport.scrollLeft >= maxScroll - 1) {
      viewport.scrollTo({ left: 0, behavior: 'smooth' });
      return;
    }

    if (direction < 0 && viewport.scrollLeft <= 1) {
      viewport.scrollTo({ left: maxScroll, behavior: 'smooth' });
      return;
    }

    viewport.scrollBy({ left: direction * pageWidth, behavior: 'smooth' });
  };

  const stopAutoplay = () => {
    if (root._bbsAutoplayTimer) {
      window.clearInterval(root._bbsAutoplayTimer);
      root._bbsAutoplayTimer = null;
    }
  };

  const startAutoplay = () => {
    stopAutoplay();
    if (!autoplayEnabled || track.scrollWidth <= viewport.clientWidth + 1) return;

    root._bbsAutoplayTimer = window.setInterval(() => {
      scrollByPage(1);
    }, autoplayInterval);
  };

  const buildDots = () => {
    if (!dotsRoot) return;

    const items = getItems();
    const visibleCount = getVisibleCount();
    const pageCount = Math.max(1, Math.ceil(items.length / visibleCount));

    dotsRoot.innerHTML = '';
    dotsRoot.hidden = pageCount <= 1;

    for (let index = 0; index < pageCount; index += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'bbs-carousel-dot';
      dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
      dot.addEventListener('click', () => {
        const pageWidth = getPageWidth();
        if (!pageWidth) return;
        stopAutoplay();
        viewport.scrollTo({ left: pageWidth * index, behavior: 'smooth' });
        startAutoplay();
      });
      dotsRoot.appendChild(dot);
    }
  };

  const sync = () => {
    const items = getItems();
    const overflow = track.scrollWidth > viewport.clientWidth + 1;
    root.classList.toggle(config.carouselClass, overflow);

    if (prevBtn) prevBtn.hidden = !overflow;
    if (nextBtn) nextBtn.hidden = !overflow;

    const maxScroll = track.scrollWidth - viewport.clientWidth;
    const left = viewport.scrollLeft;
    if (prevBtn) prevBtn.disabled = left <= 1;
    if (nextBtn) nextBtn.disabled = left >= maxScroll - 1;

    if (dotsRoot) {
      buildDots();
      const visibleCount = getVisibleCount();
      const pageWidth = getPageWidth();
      const activeIndex = pageWidth > 0 ? Math.floor(left / pageWidth) : 0;
      dotsRoot.querySelectorAll('.bbs-carousel-dot').forEach((dot, index) => {
        dot.classList.toggle('is-active', index === activeIndex);
      });
    }

    if (!overflow) {
      stopAutoplay();
    } else if (!root._bbsAutoplayTimer && autoplayEnabled && !root.dataset.bbsCarouselPaused) {
      startAutoplay();
    }
  };

  let dragActive = false;
  let dragStartX = 0;
  let dragStartScrollLeft = 0;

  const onPointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (track.scrollWidth <= viewport.clientWidth + 1) return;

    dragActive = true;
    dragStartX = event.clientX;
    dragStartScrollLeft = viewport.scrollLeft;
    root.dataset.bbsCarouselPaused = 'true';
    stopAutoplay();
    viewport.classList.add('is-dragging');
    viewport.setPointerCapture?.(event.pointerId);
  };

  const onPointerMove = (event) => {
    if (!dragActive) return;
    viewport.scrollLeft = dragStartScrollLeft - (event.clientX - dragStartX);
  };

  const endDrag = (event) => {
    if (!dragActive) return;

    dragActive = false;
    viewport.classList.remove('is-dragging');
    if (event?.pointerId != null) {
      viewport.releasePointerCapture?.(event.pointerId);
    }
    delete root.dataset.bbsCarouselPaused;
    sync();
    startAutoplay();
  };

  prevBtn?.addEventListener('click', () => {
    stopAutoplay();
    scrollByPage(-1);
    startAutoplay();
  });

  nextBtn?.addEventListener('click', () => {
    stopAutoplay();
    scrollByPage(1);
    startAutoplay();
  });

  viewport.addEventListener('pointerdown', onPointerDown);
  viewport.addEventListener('pointermove', onPointerMove);
  viewport.addEventListener('pointerup', endDrag);
  viewport.addEventListener('pointercancel', endDrag);
  viewport.addEventListener('scroll', sync, { passive: true });

  root.addEventListener('mouseenter', stopAutoplay);
  root.addEventListener('mouseleave', () => {
    delete root.dataset.bbsCarouselPaused;
    startAutoplay();
  });

  root.addEventListener('focusin', stopAutoplay);
  root.addEventListener('focusout', (event) => {
    if (!root.contains(event.relatedTarget)) {
      delete root.dataset.bbsCarouselPaused;
      startAutoplay();
    }
  });

  window.addEventListener('resize', sync);
  sync();
  startAutoplay();
}

function scanHomepageCarousels(scope = document) {
  scope.querySelectorAll('[data-bbs-journal-carousel]').forEach((root) => {
    initHorizontalCarousel(root, {
      viewportSelector: '[data-bbs-journal-viewport]',
      trackSelector: '[data-bbs-journal-track]',
      prevSelector: '[data-bbs-journal-prev]',
      nextSelector: '[data-bbs-journal-next]',
      itemSelector: '.bbs-journal-card',
      carouselClass: 'bbs-journal__frame--active'
    });
  });

  scope.querySelectorAll('[data-bbs-testimonials-carousel]').forEach((root) => {
    initHorizontalCarousel(root, {
      viewportSelector: '[data-bbs-testimonials-viewport]',
      trackSelector: '[data-bbs-testimonials-track]',
      prevSelector: '[data-bbs-testimonials-prev]',
      nextSelector: '[data-bbs-testimonials-next]',
      dotsSelector: '[data-bbs-testimonials-dots]',
      itemSelector: '.bbs-testimonial-card',
      carouselClass: 'bbs-testimonials__frame--active'
    });
  });
}

document.addEventListener('DOMContentLoaded', () => {
  scanHomepageCarousels();
});

document.addEventListener('shopify:section:load', (event) => {
  scanHomepageCarousels(event.target);
});

window.BBSScanHomepageCarousels = scanHomepageCarousels;
